// 访客 SDK 的桌面通知 + 提示音工具。
//
// 设计目标：
//  - 浮窗关闭 / 标签页隐藏 时收到对方消息能引起访客注意
//  - 不依赖任何外部资源（图标 / 音频文件）：通知图标可选；提示音用内置短音 base64
//  - Notification 权限按需请求：首次需要时才弹系统权限框，避免上来就请求
//
// 工作台已有 lib/notify.ts；这里是独立的最小实现，避免 SDK 引入工作台依赖。

let audioCtx: AudioContext | null = null;
// 一次性的权限请求 promise，避免并发请求时多次弹系统提示
let permissionRequest: Promise<NotificationPermission> | null = null;

/** 是否处于「页面不可见或浮窗未打开」的状态——这是判断是否应触发通知的入口。
 *  widgetOpen 由调用方传入：true 表示访客浮窗正打开。 */
export function shouldNotify(widgetOpen: boolean): boolean {
  if (widgetOpen) return false;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return true;
  // 浮窗未打开且页面可见时也提示——用户可能在看其它 tab 或滚到了页面底部
  return true;
}

/** 播放一段短促的"叮"声。失败静默（如自动播放策略阻止）。 */
export function playBeep(): void {
  if (typeof window === 'undefined') return;
  try {
    const AC =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    if (!audioCtx) audioCtx = new AC();
    // suspended（首次未交互前）尝试 resume；失败也无所谓
    if (audioCtx.state === 'suspended') {
      void audioCtx.resume();
    }
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.type = 'sine';
    o.frequency.value = 880; // A5
    g.gain.setValueAtTime(0.001, audioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, audioCtx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.22);
    o.connect(g).connect(audioCtx.destination);
    o.start();
    o.stop(audioCtx.currentTime + 0.25);
  } catch {
    /* ignore */
  }
}

/** 弹桌面通知。permission=default 时先请求一次；denied 时静默。
 *  @param onClick 用户点击通知时调用（一般用于聚焦页面 / 打开浮窗） */
export async function showNotification(
  title: string,
  body: string,
  tag?: string,
  onClick?: () => void,
): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  let perm = Notification.permission;
  if (perm === 'default') {
    if (!permissionRequest) {
      permissionRequest = Notification.requestPermission().catch(() => 'denied' as NotificationPermission);
    }
    perm = await permissionRequest;
  }
  if (perm !== 'granted') return;
  try {
    const n = new Notification(title, { body, tag });
    if (onClick) {
      n.onclick = () => {
        try {
          window.focus();
          onClick();
        } finally {
          n.close();
        }
      };
    }
    // 5s 后自动关
    window.setTimeout(() => n.close(), 5000);
  } catch {
    /* ignore */
  }
}
